import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, gql } from '@apollo/client';
import { 
  ArrowLeft, 
  MessageCircle, 
  Phone, 
  Mail, 
  Clock, 
  Package,
  Truck,
  CreditCard,
  Users,
  ChevronDown,
  ChevronUp,
  Send,
  AlertCircle,
  Check
} from 'lucide-react';

// GraphQL mutation for submitting support tickets
const CREATE_SUPPORT_TICKET = gql`
  mutation CreateSupportTicket($input: SupportTicketInput!) {
    createSupportTicket(input: $input) {
      id
      status
      createdAt
    }
  }
`;

const CustomerSupportPage = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [ticketSubmitted, setTicketSubmitted] = useState(false);
  
  const [createTicket, { loading }] = useMutation(CREATE_SUPPORT_TICKET, {
    onCompleted: () => {
      setTicketSubmitted(true);
      setMessage('');
      setSelectedCategory('');
    },
    onError: (error) => {
      console.error('Error submitting ticket:', error);
    }
  });

  const faqs = [
    {
      category: 'Orders',
      icon: <Package size={20} />,
      questions: [
        {
          q: 'How do I track my order?',
          a: 'You can track your order by going to "My Orders" from the main menu. Each order shows its current status and estimated delivery time.'
        },
        {
          q: 'Can I cancel my order?',
          a: 'Orders can be cancelled if they are still in "Pending" or "Confirmed" status. Go to My Orders and click the Cancel button next to your order.'
        },
        {
          q: 'When will my order arrive?',
          a: 'Delivery typically takes 2-3 business days. Pickup orders are usually ready within 24 hours. You\'ll receive a notification when your order is ready.'
        }
      ]
    },
    {
      category: 'Delivery',
      icon: <Truck size={20} />,
      questions: [
        {
          q: 'What are the delivery fees?',
          a: 'Delivery fees are $5.99 for standard delivery. You can save on delivery fees by choosing pickup, which also earns you 2 extra tokens!'
        },
        {
          q: 'Do you deliver to my area?',
          a: 'We currently deliver within a 15-mile radius of Sacramento. Enter your address at checkout to confirm delivery availability.'
        },
        {
          q: 'Can I change my delivery address?',
          a: 'You can change your delivery address before your order is confirmed. Contact support immediately if you need to make changes.'
        }
      ]
    },
    {
      category: 'Payments',
      icon: <CreditCard size={20} />,
      questions: [
        {
          q: 'What payment methods do you accept?',
          a: 'We accept all major credit and debit cards through our secure Stripe payment system.'
        },
        {
          q: 'Is my payment information secure?',
          a: 'Yes! We use Stripe for payment processing, which is PCI-compliant and uses bank-level encryption to protect your information.'
        },
        {
          q: 'Can I get a refund?',
          a: 'Refunds are available for cancelled orders or if there\'s an issue with your order. Contact support for assistance.'
        }
      ]
    },
    {
      category: 'Groups & Tokens',
      icon: <Users size={20} />,
      questions: [
        {
          q: 'How do tokens work?',
          a: 'Tokens are rewards you earn from purchases. Earn 5 tokens for delivery orders and 7 for pickup orders. Tokens can be used for discounts on future purchases.'
        },
        {
          q: 'What are buying groups?',
          a: 'Buying groups allow members to pool their purchases for bulk discounts. Join or create a group to save money on regular purchases.'
        },
        {
          q: 'How do I join a group?',
          a: 'Browse available groups in the Groups section and click "Join Group". Some groups may require approval from the group admin.'
        }
      ]
    }
  ];

  const supportCategories = [
    { value: 'order_issue', label: 'Order Issue' },
    { value: 'delivery_problem', label: 'Delivery Problem' },
    { value: 'payment_issue', label: 'Payment Issue' },
    { value: 'product_quality', label: 'Product Quality' },
    { value: 'group_question', label: 'Group Question' },
    { value: 'account_help', label: 'Account Help' },
    { value: 'other', label: 'Other' }
  ];

  const handleSubmitTicket = async () => {
    if (!selectedCategory || !message || !email) {
      alert('Please fill in all fields');
      return;
    }

    try {
      await createTicket({
        variables: {
          input: {
            category: selectedCategory,
            message: message,
            email: email
          }
        }
      });
    } catch (error) {
      alert('Failed to submit ticket. Please try again.');
    }
  };

  const toggleFaq = (category, index) => {
    const key = `${category}-${index}`;
    setExpandedFaq(expandedFaq === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="px-4 py-4 flex items-center">
          <button 
            onClick={() => navigate(-1)}
            className="mr-3 p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-lg font-semibold">Customer Support</h1>
            <p className="text-sm text-gray-600">We're here to help</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Quick Contact Options */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Contact Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg">
              <MessageCircle className="text-blue-600" size={24} />
              <div>
                <p className="font-medium">Live Chat</p>
                <p className="text-sm text-gray-600">Available 9am-6pm PST</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg">
              <Phone className="text-green-600" size={24} />
              <div>
                <p className="font-medium">Call Us</p>
                <p className="text-sm text-gray-600">(916) 500-4684</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-4 bg-purple-50 rounded-lg">
              <Mail className="text-purple-600" size={24} />
              <div>
                <p className="font-medium">Email</p>
                <p className="text-sm text-gray-600">support@uniledge.io</p>
              </div>
            </div>
          </div>
          
          <div className="mt-4 flex items-center text-sm text-gray-600">
            <Clock size={16} className="mr-2" />
            Average response time: 2 hours
          </div>
        </div>

        {/* FAQs */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            {faqs.map((category) => (
              <div key={category.category} className="border-b last:border-b-0 pb-4 last:pb-0">
                <div className="flex items-center mb-3">
                  <span className="text-gray-600 mr-2">{category.icon}</span>
                  <h3 className="font-medium text-gray-900">{category.category}</h3>
                </div>
                
                <div className="space-y-2">
                  {category.questions.map((item, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg">
                      <button
                        onClick={() => toggleFaq(category.category, index)}
                        className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <span className="font-medium text-sm">{item.q}</span>
                        {expandedFaq === `${category.category}-${index}` ? (
                          <ChevronUp size={16} className="text-gray-500 flex-shrink-0" />
                        ) : (
                          <ChevronDown size={16} className="text-gray-500 flex-shrink-0" />
                        )}
                      </button>
                      
                      {expandedFaq === `${category.category}-${index}` && (
                        <div className="px-4 pb-3">
                          <p className="text-sm text-gray-600">{item.a}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Submit Ticket Form */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Submit a Support Ticket</h2>
          
          {ticketSubmitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Ticket Submitted!</h3>
              <p className="text-gray-600 mb-4">
                We've received your request and will respond within 24 hours.
              </p>
              <button
                onClick={() => setTicketSubmitted(false)}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Submit Another Ticket
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="your@email.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Issue Category *
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a category</option>
                  {supportCategories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Describe Your Issue *
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Please provide details about your issue..."
                />
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start">
                <AlertCircle size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-700">
                  For urgent issues, please call our support line at (916) 500-4684
                </p>
              </div>
              
              <button
                onClick={handleSubmitTicket}
                disabled={loading || !selectedCategory || !message || !email}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {loading ? (
                  <span>Submitting...</span>
                ) : (
                  <>
                    <Send size={20} className="mr-2" />
                    Submit Ticket
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerSupportPage;